import React, { useMemo } from 'react';
import { ILMSFormSection, ILMSFormField, ILMSInput, ILMSSelect, ILMSTextarea } from '../../components/ILMSForm';
import { ILMSDatePicker } from '../../components/ILMSDatePicker';
import { ILMSAnimalPicker } from '../../components/ILMSAnimalPicker';
import { ILMSAnimalMultiPicker } from '../../components/ILMSAnimalMultiPicker';
import { ILMSPageAccessPicker } from '../../components/ILMSPageAccessPicker';
import { ILMSAttachmentPreview } from '../../components/ILMSAttachmentPreview';
import type { FieldConfig } from './types';
import type { RefKey, RefOption } from './referenceData';

export interface GenericFormProps<T extends object> {
  fields: FieldConfig<T>[];
  values: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
  refData?: Record<RefKey, RefOption[]>;
  errors?: Record<string, string>;
  /** Columns per section title (default 2), e.g. Quantity · Fat % · Rate on one line. */
  sectionColumns?: Record<string, 1 | 2 | 3>;
}

export function GenericForm<T extends object>({ fields, values, onChange, refData, errors = {}, sectionColumns }: GenericFormProps<T>) {
  const sections = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, FieldConfig<T>[]>();
    fields.forEach((f) => {
      if (!map.has(f.section)) {
        map.set(f.section, []);
        order.push(f.section);
      }
      map.get(f.section)!.push(f);
    });
    return order.map((section) => ({ section, fields: map.get(section)! }));
  }, [fields]);

  return (
    <div className="flex flex-col gap-6">
      {sections.map(({ section, fields: sectionFields }) => {
        const visible = sectionFields.filter((f) => !f.visibleWhen || f.visibleWhen(values));
        if (visible.length === 0) return null;
        return (
          <ILMSFormSection key={section} title={section} columns={sectionColumns?.[section]}>
            {visible.map((field) => (
              <ILMSFormField
                key={field.name}
                label={field.label}
                htmlFor={field.name}
                required={typeof field.required === 'function' ? field.required(values) : field.required}
                optional={field.optional}
                autoCalculated={field.autoCalculated}
                systemGenerated={field.systemGenerated}
                error={errors[field.name]}
                helperText={field.helperText}
                action={field.onAction ? { label: field.actionLabel ?? 'Add new', onClick: field.onAction } : undefined}
                fullWidth={field.fullWidth}
              >
                {renderControl(field, values, onChange, refData, !!errors[field.name])}
              </ILMSFormField>
            ))}
          </ILMSFormSection>
        );
      })}
    </div>
  );
}

function renderControl<T extends object>(
  field: FieldConfig<T>,
  values: Record<string, unknown>,
  onChange: (name: string, value: unknown) => void,
  refData: Record<RefKey, RefOption[]> | undefined,
  hasError: boolean
) {
  const raw = values[field.name];
  const stringValue = raw === undefined || raw === null ? '' : String(raw);

  switch (field.type) {
    case 'display':
      return (
        <ILMSInput
          id={field.name}
          value={field.computeDisplay ? field.computeDisplay(values) : stringValue}
          disabled
          readOnly
        />
      );
    case 'textarea':
      return (
        <ILMSTextarea
          id={field.name}
          value={stringValue}
          placeholder={field.placeholder}
          error={hasError}
          onChange={(e) => onChange(field.name, e.target.value)}
        />
      );
    case 'date':
      return (
        <ILMSDatePicker
          id={field.name}
          value={stringValue}
          error={hasError ? ' ' : undefined}
          onChange={(e) => onChange(field.name, e.target.value)}
        />
      );
    case 'select': {
      const resolvedKey = typeof field.optionsKey === 'function' ? field.optionsKey(values) : field.optionsKey;
      const allOptions = resolvedKey && refData ? refData[resolvedKey] : field.staticOptions ?? [];
      const options = field.filterOptions ? field.filterOptions(allOptions, values) : allOptions;
      // Hundreds of animals can exist — a scrolling <select> is painful, so
      // this one field type gets a search + QR-scan picker instead.
      if (resolvedKey === 'animals') {
        return (
          <ILMSAnimalPicker
            id={field.name}
            value={stringValue}
            onChange={(v) => onChange(field.name, v)}
            options={options}
            error={hasError}
          />
        );
      }
      return (
        <ILMSSelect
          id={field.name}
          value={stringValue}
          error={hasError}
          onChange={(e) => onChange(field.name, e.target.value)}
        >
          <option value="">Select {field.label.toLowerCase()}…</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </ILMSSelect>
      );
    }
    case 'multiselect': {
      const resolvedKey = typeof field.optionsKey === 'function' ? field.optionsKey(values) : field.optionsKey;
      const options = resolvedKey && refData ? refData[resolvedKey] : field.staticOptions ?? [];
      const selected = Array.isArray(raw) ? (raw as string[]) : [];
      return (
        <ILMSAnimalMultiPicker
          id={field.name}
          value={selected}
          onChange={(v) => onChange(field.name, v)}
          options={options}
          error={hasError}
        />
      );
    }
    case 'pageAccess': {
      const selected = Array.isArray(raw) ? (raw as string[]) : [];
      return <ILMSPageAccessPicker value={selected} onChange={(v) => onChange(field.name, v)} />;
    }
    case 'file': {
      const file = raw as File | null | undefined;
      const existingId = field.existingAttachmentKey ? (values[field.existingAttachmentKey] as string | null | undefined) : null;
      return (
        <div className="flex flex-col gap-2">
          {!file && existingId && <ILMSAttachmentPreview attachmentId={existingId} />}
          <input
            id={field.name}
            type="file"
            accept="image/*"
            onChange={(e) => onChange(field.name, e.target.files?.[0] ?? null)}
            className="text-body text-ink-700 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-brand-green-50 file:text-brand-green-800 file:text-label hover:file:bg-brand-green-100"
          />
          {file && <p className="text-helper-text text-ink-500">Will replace the current photo with: {file.name}</p>}
        </div>
      );
    }
    case 'checkbox':
      return (
        <label className="flex items-center gap-2 h-11">
          <input
            id={field.name}
            type="checkbox"
            checked={!!raw}
            onChange={(e) => onChange(field.name, e.target.checked)}
            className="h-5 w-5 rounded border-ink-300/50 text-brand-green-700 focus-visible:outline-2 focus-visible:outline-brand-green-500"
          />
          <span className="text-body text-ink-700">{field.placeholder ?? 'Yes'}</span>
        </label>
      );
    case 'number':
    case 'decimal':
      return (
        <ILMSInput
          id={field.name}
          type="number"
          inputMode="decimal"
          step={field.step ?? (field.type === 'decimal' ? 0.01 : 1)}
          min={field.min}
          value={stringValue}
          placeholder={field.placeholder}
          error={hasError}
          onChange={(e) => onChange(field.name, e.target.value === '' ? '' : Number(e.target.value))}
        />
      );
    case 'password':
      return (
        <ILMSInput
          id={field.name}
          type="password"
          autoComplete="new-password"
          value={stringValue}
          placeholder={field.placeholder}
          error={hasError}
          onChange={(e) => onChange(field.name, e.target.value)}
        />
      );
    case 'text':
    default:
      return (
        <ILMSInput
          id={field.name}
          type="text"
          value={stringValue}
          placeholder={field.placeholder}
          error={hasError}
          onChange={(e) => onChange(field.name, e.target.value)}
        />
      );
  }
}
